import { ChannelIcon, ErrorState, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

interface ConsentRecord {
  id: string;
  identityId: string;
  identityType: string;
  identityValue: string;
  channel: string;
  purpose: 'marketing' | 'service';
  status: 'granted' | 'withdrawn';
  source: string;
  evidence: { note?: string | null; messageId?: string; text?: string };
  recordedAt: string;
  recordedBy: string | null;
}

/** Channels a handle type can be contacted on (mirrors the server's rule). */
const CHANNELS_FOR: Record<string, string[]> = {
  phone: ['whatsapp', 'sms'],
  wa_bsuid: ['whatsapp'],
  email: ['email'],
};

/** Marketing consent per handle and channel, with the ledger's history as proof. */
export function ConsentPanel({
  personId,
  identities,
}: {
  personId: string;
  identities: { id: string; type: string; value: string }[];
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { can } = useAuth();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['consent', personId],
    queryFn: () => api<{ items: ConsentRecord[] }>(`/v1/people/${personId}/consent`),
  });
  const record = useMutation({
    mutationFn: (b: { identityId: string; channel: string; status: 'granted' | 'withdrawn' }) =>
      api(`/v1/people/${personId}/consent`, {
        method: 'POST',
        body: JSON.stringify({ ...b, purpose: 'marketing' }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['consent', personId] });
      void qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
  const rows = identities.flatMap((i) => (CHANNELS_FOR[i.type] ?? []).map((channel) => ({ ...i, channel })));
  if (!rows.length) return null;
  const latest = (identityId: string, channel: string) =>
    q.data?.items.find(
      (r) => r.identityId === identityId && r.channel === channel && r.purpose === 'marketing',
    );

  return (
    <section className="card" aria-label={t('contacts.consentTitle')} data-testid="consent-panel">
      <div className="card-head">
        <h2>{t('contacts.consentTitle')}</h2>
      </div>
      <ul className="list-plain">
        {rows.map((r) => {
          const cur = latest(r.id, r.channel);
          const status = cur?.status ?? 'unknown';
          return (
            <li key={`${r.id}-${r.channel}`} data-consent-row={`${r.channel}:${status}`}>
              <span className="channel-badge">
                <ChannelIcon channel={r.channel} size={12} />
              </span>
              <div className="grow" style={{ minWidth: 0 }}>
                <div
                  className="mono ellipsis"
                  dir="ltr"
                  style={{ fontSize: 'var(--t-xs)', textAlign: 'start' }}
                >
                  {r.value}
                </div>
                <div className="faint" style={{ fontSize: '0.72rem' }}>
                  {t(`contacts.consent.${status}`)}
                  {cur &&
                    ` · ${fmt.dateTime(cur.recordedAt)} · ${
                      cur.source === 'customer_message'
                        ? t('contacts.byCustomerMessage')
                        : (cur.recordedBy ?? '')
                    }`}
                </div>
              </div>
              {can('persons:write') && (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  disabled={record.isPending}
                  onClick={() =>
                    record.mutate({
                      identityId: r.id,
                      channel: r.channel,
                      status: status === 'granted' ? 'withdrawn' : 'granted',
                    })
                  }
                >
                  {status === 'granted' ? t('contacts.withdraw') : t('contacts.grant')}
                </button>
              )}
            </li>
          );
        })}
      </ul>
      <p className="faint" style={{ fontSize: '0.72rem', margin: '0 1.25rem 1rem' }}>
        {t('contacts.consentHint')}
      </p>
      {record.isError && <ErrorState error={record.error} />}
    </section>
  );
}
